import { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import { PDFDocument } from 'pdf-lib';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { useApp } from '@/store/app';
import { readFileAsArrayBuffer, bytesToBlob } from '@/lib/utils';

export function PdfSplit() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [pages, setPages] = useState('1-3, 5');
  const [mode, setMode] = useState<'extract' | 'every'>('extract');
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (pendingFile && pendingFile.type === 'application/pdf') {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    const buf = await readFileAsArrayBuffer(f);
    const doc = await PDFDocument.load(buf);
    setPageCount(doc.getPageCount());
  }

  function parsePages(input: string, total: number): number[] {
    const out = new Set<number>();
    for (const part of input.split(',')) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const match = trimmed.match(/^(\d+)(?:-(\d+))?$/);
      if (!match) continue;
      const a = parseInt(match[1], 10) - 1;
      const b = match[2] ? parseInt(match[2], 10) - 1 : a;
      for (let i = a; i <= b; i++) {
        if (i >= 0 && i < total) out.add(i);
      }
    }
    return [...out].sort((a, b) => a - b);
  }

  async function run() {
    if (!file) return;
    setWorking(true);
    try {
      const buf = await readFileAsArrayBuffer(file);
      const src = await PDFDocument.load(buf);
      const total = src.getPageCount();

      if (mode === 'extract') {
        const indices = parsePages(pages, total);
        if (!indices.length) {
          toast.error('Invalid page range');
          return;
        }
        const out = await PDFDocument.create();
        const copied = await out.copyPages(src, indices);
        copied.forEach((p) => out.addPage(p));
        const bytes = await out.save();
        saveAs(
          bytesToBlob(bytes),
          file.name.replace(/\.pdf$/i, '') + '-pages.pdf',
        );
        toast.success(`Extracted ${indices.length} ${indices.length === 1 ? 'page' : 'pages'}`);
      } else {
        for (let i = 0; i < total; i++) {
          const out = await PDFDocument.create();
          const [copied] = await out.copyPages(src, [i]);
          out.addPage(copied);
          const bytes = await out.save();
          saveAs(
            bytesToBlob(bytes),
            `${file.name.replace(/\.pdf$/i, '')}-page-${i + 1}.pdf`,
          );
        }
        toast.success(`Saved ${total} files`);
      }
    } catch (e) {
      toast.error('Split failed');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="pdf-split"
        title="Split PDF"
        description="Extract specific pages or split a PDF into individual files — one page each."
        badges={pageCount > 0 ? <Badge>{pageCount} pages</Badge> : null}
      />

      {!file ? (
        <Dropzone
          accept="application/pdf"
          onFiles={handleFile}
          hint="Single PDF · processed locally"
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 !p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
              <span className="text-sm font-medium truncate">{file.name}</span>
              <Button size="sm" variant="ghost" onClick={() => setFile(null)}>
                Change
              </Button>
            </div>
            <div className="p-5">
              <Tabs value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
                <TabsList className="mb-4">
                  <TabsTrigger value="extract">Extract pages</TabsTrigger>
                  <TabsTrigger value="every">Every page → file</TabsTrigger>
                </TabsList>
                <TabsContent value="extract">
                  <Field label="Pages" hint={`Total: ${pageCount}`}>
                    <Input
                      value={pages}
                      onChange={(e) => setPages(e.target.value)}
                      placeholder="e.g. 1-3, 5, 7-9"
                      className="font-mono"
                    />
                  </Field>
                  <div className="text-2xs text-fg-subtle mt-2">
                    Use commas to list, dashes for ranges. Pages are 1-indexed.
                  </div>
                </TabsContent>
                <TabsContent value="every">
                  <div className="text-sm text-fg-muted">
                    Each page becomes its own PDF file ({pageCount} downloads).
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </Card>

          <Card>
            <div className="text-xs font-medium text-fg-muted mb-3">Summary</div>
            <Button
              variant="primary"
              className="w-full"
              disabled={working || !pageCount}
              loading={working}
              onClick={run}
            >
              <Download size={12} />
              Split
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}

export default PdfSplit;
